import React, { useEffect, useState } from 'react';
import {
  onSessionConflict,
  hasSessionConflictPending,
  clearSessionConflictPending,
} from '../services/sessionConflictEvent';
import { clearAuthAndRedirectToLogin } from '../services/authNavigation';

/** Aligns with portal theme: purple #942780, light surfaces (dashboard / exam submitted). */
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(74, 20, 60, 0.35)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    animation: 'jsyog-fade-in 0.35s ease',
  },
  card: {
    background: '#fff',
    border: '1px solid #f2dcec',
    borderRadius: '14px',
    padding: '40px 36px 36px',
    maxWidth: '460px',
    width: '90%',
    boxShadow: '0 2px 10px rgba(111, 0, 84, 0.12), 0 12px 40px rgba(111, 0, 84, 0.08)',
    textAlign: 'center',
    animation: 'jsyog-slide-up 0.4s cubic-bezier(0.22,1,0.36,1)',
    position: 'relative',
    overflow: 'hidden',
  },
  glow: {
    position: 'absolute',
    top: '-40px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '220px',
    height: '180px',
    background: 'radial-gradient(circle, rgba(148, 39, 128, 0.12) 0%, transparent 72%)',
    pointerEvents: 'none',
  },
  iconWrap: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: '#f5e5f1',
    border: '2px solid rgba(148, 39, 128, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  icon: {
    fontSize: '32px',
    lineHeight: 1,
  },
  title: {
    color: '#942780',
    fontSize: '22px',
    fontWeight: 700,
    margin: '0 0 12px',
    fontFamily: 'inherit',
    letterSpacing: '-0.02em',
  },
  message: {
    color: '#606060',
    fontSize: '15px',
    lineHeight: 1.65,
    margin: '0 0 32px',
    fontFamily: 'inherit',
  },
  okButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    background: '#942780',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 40px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: 'inherit',
    letterSpacing: '0.02em',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(148, 39, 128, 0.35)',
    transition:
      'transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, opacity 0.15s ease',
    outline: 'none',
  },
};

// Inject keyframe animations once
let animationsInjected = false;
function injectAnimations() {
  if (animationsInjected || typeof document === 'undefined') return;
  animationsInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes jsyog-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes jsyog-slide-up {
      from { opacity: 0; transform: translateY(32px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0)   scale(1); }
    }
    #jsyog-ok-btn:hover {
      transform: translateY(-1px) !important;
      filter: brightness(1.08) !important;
      box-shadow: 0 8px 22px rgba(148, 39, 128, 0.45) !important;
    }
    #jsyog-ok-btn:active {
      transform: translateY(0) !important;
      opacity: 0.92 !important;
    }
  `;
  document.head.appendChild(style);
}

export function SessionConflictBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    injectAnimations();

    // ✅ Restore conflict state from localStorage on mount — survives page refresh.
    // Device 1 will keep seeing the popup even after reloading until OK is clicked.
    if (hasSessionConflictPending()) {
      setVisible(true);
    }

    const unsubscribe = onSessionConflict(() => {
      setVisible(true);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  function handleOk() {
    clearSessionConflictPending();
    clearAuthAndRedirectToLogin('device_conflict');
  }

  if (!visible) return null;

  return (
    <div style={styles.overlay} role="alertdialog" aria-modal="true" aria-live="assertive">
      <div style={styles.card}>
        {/* Glow effect */}
        <div style={styles.glow} />

        {/* Icon */}
        <div style={styles.iconWrap}>
          <span style={styles.icon}>🔒</span>
        </div>

        {/* Title */}
        <h2 style={styles.title}>Session Conflict Detected</h2>

        {/* Message */}
        <p style={styles.message}>
          Your account has been logged in on{' '}
          <strong style={{ color: '#942780' }}>another device</strong>.
          <br />
          For your security, this session will be logged out.
          <br />
          Please click <strong style={{ color: '#942780' }}>OK</strong> to continue.
        </p>

        {/* OK button — no auto-redirect, user must acknowledge */}
        <button id="jsyog-ok-btn" style={styles.okButton} onClick={handleOk} autoFocus>
          OK, Log Me Out
        </button>
      </div>
    </div>
  );
}

export default SessionConflictBanner;
